import React, { useMemo } from 'react';
import { View, Text, ScrollView, TextInput, Pressable, ActivityIndicator, StyleSheet } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { CommentThread, CommentContextType } from '../../shared/types/comments';
import { addColorOpacity } from '../../shared/utils/styles';
import { ThreadCard } from './ThreadCard';
import { Ionicons } from '@expo/vector-icons';

interface ThreadListProps {
  threads: CommentThread[];
  loading?: boolean;
  filter?: CommentContextType | 'all';
  onFilterChange?: (filter: CommentContextType | 'all') => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onThreadPress: (thread: CommentThread) => void;
  emptyStateTitle?: string;
  emptyStateMessage?: string;
}

const getContextLabel = (contextType: CommentContextType): string => {
  const labels: Record<CommentContextType, string> = {
    portfolio: 'Portfolio',
    property: 'Property',
    financial_entry: 'Financial',
    mortgage_calculation: 'Mortgage',
    due_diligence_item: 'Due Diligence',
    survey_item: 'Survey',
    meeting: 'Meeting',
    task: 'Task',
    document: 'Document',
  };
  return labels[contextType] || contextType;
};

export function ThreadList({
  threads,
  loading = false,
  filter = 'all',
  onFilterChange,
  searchQuery = '',
  onSearchChange,
  onThreadPress,
  emptyStateTitle,
  emptyStateMessage,
}: ThreadListProps) {
  const styles = useStyles();
  const responsive = useResponsive();
  const { isMobile } = responsive;

  // Filter threads
  const filteredThreads = useMemo(() => {
    let result = threads;

    // Apply context filter
    if (filter !== 'all') {
      result = result.filter((t) => t.context_type === filter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.thread_title?.toLowerCase().includes(query) ||
          t.context_name?.toLowerCase().includes(query) ||
          t.created_by_user?.email?.toLowerCase().includes(query)
      );
    }

    // Sort by updated_at (most recent first)
    return result.sort((a, b) => {
      const dateA = new Date(a.updated_at).getTime();
      const dateB = new Date(b.updated_at).getTime();
      return dateB - dateA;
    });
  }, [threads, filter, searchQuery]);

  const contextTypes: (CommentContextType | 'all')[] = [
    'all',
    'portfolio',
    'property',
    'mortgage_calculation',
    'financial_entry',
    'due_diligence_item',
  ];

  return (
    <View style={{ flex: 1, minHeight: 0 }}>
      {/* Search Bar */}
      {onSearchChange && (
        <View style={{ marginBottom: styles.utils.spacing.md }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: styles.tokens.color.surface,
              borderRadius: styles.utils.radius.md,
              borderWidth: 1,
              borderColor: styles.tokens.color.border,
              paddingHorizontal: styles.utils.spacing.md,
            }}
          >
            <Ionicons
              name="search"
              size={18}
              color={addColorOpacity(styles.tokens.color.onSurface, 0.5)}
              style={{ marginRight: styles.utils.spacing.sm }}
            />
            <TextInput
              style={{
                flex: 1,
                paddingVertical: styles.utils.spacing.sm,
                fontSize: styles.utils.typography.sizes.base,
                color: styles.tokens.color.onSurface,
                fontFamily: styles.utils.typography.body.fontFamily,
              }}
              placeholder="Search discussions..."
              placeholderTextColor={addColorOpacity(styles.tokens.color.onSurface, 0.5)}
              value={searchQuery}
              onChangeText={onSearchChange}
            />
            {searchQuery.length > 0 && (
              <Pressable onPress={() => onSearchChange('')}>
                <Ionicons
                  name="close-circle"
                  size={18}
                  color={addColorOpacity(styles.tokens.color.onSurface, 0.5)}
                />
              </Pressable>
            )}
          </View>
        </View>
      )}

      {/* Filter Tabs */}
      {onFilterChange && (
        <View style={{ marginBottom: styles.utils.spacing.md }}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: styles.utils.spacing.xs,
              gap: styles.utils.spacing.xs,
            }}
          >
            {contextTypes.map((type) => (
              <Pressable
                key={type}
                style={{
                  paddingHorizontal: styles.utils.spacing.md,
                  paddingVertical: styles.utils.spacing.sm,
                  borderRadius: styles.utils.radius.full,
                  backgroundColor:
                    filter === type ? styles.tokens.color.primary : styles.tokens.color.surface,
                  borderWidth: 1,
                  borderColor:
                    filter === type ? styles.tokens.color.primary : styles.tokens.color.border,
                  minHeight: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onPress={() => onFilterChange(type)}
              >
                <Text
                  style={{
                    fontSize: styles.utils.typography.sizes.sm,
                    color:
                      filter === type
                        ? styles.tokens.color.onPrimary
                        : styles.tokens.color.onSurface,
                    fontWeight:
                      filter === type
                        ? styles.utils.typography.weights.semibold
                        : styles.utils.typography.weights.medium,
                    fontFamily: styles.utils.typography.body.fontFamily,
                  }}
                >
                  {type === 'all' ? 'All' : getContextLabel(type)}
                </Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Threads List */}
      {loading && filteredThreads.length === 0 ? (
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            paddingVertical: styles.utils.spacing['2xl'],
          }}
        >
          <ActivityIndicator size="large" color={styles.tokens.color.primary} />
          <Text
            style={{
              marginTop: styles.utils.spacing.md,
              fontSize: styles.utils.typography.sizes.base,
              color: styles.tokens.color.onSurface,
              fontFamily: styles.utils.typography.body.fontFamily,
            }}
          >
            Loading discussions...
          </Text>
        </View>
      ) : filteredThreads.length === 0 ? (
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            paddingVertical: styles.utils.spacing['2xl'],
            alignItems: 'center',
          }}
        >
          <Ionicons
            name="chatbubbles-outline"
            size={48}
            color={addColorOpacity(styles.tokens.color.onSurface, 0.3)}
            style={{ marginBottom: styles.utils.spacing.md }}
          />
          <Text
            style={{
              fontSize: styles.utils.typography.sizes.lg,
              fontWeight: styles.utils.typography.weights.semibold,
              color: styles.tokens.color.onSurface,
              marginBottom: styles.utils.spacing.xs,
              fontFamily: styles.utils.typography.body.fontFamily,
              textAlign: 'center',
            }}
          >
            {emptyStateTitle || (searchQuery ? 'No discussions match your search' : 'No discussions yet')}
          </Text>
          <Text
            style={{
              fontSize: styles.utils.typography.sizes.base,
              color: addColorOpacity(styles.tokens.color.onSurface, 0.6),
              textAlign: 'center',
              fontFamily: styles.utils.typography.body.fontFamily,
            }}
          >
            {emptyStateMessage ||
              (searchQuery
                ? 'Try adjusting your search or filters'
                : 'Start a discussion on any property or portfolio')}
          </Text>
        </ScrollView>
      ) : (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ 
            paddingBottom: styles.utils.spacing.md,
          }}
          showsVerticalScrollIndicator={true}
        >
          {filteredThreads.map((thread) => (
            <ThreadCard key={thread.id} thread={thread} onPress={onThreadPress} />
          ))}
        </ScrollView>
      )}
    </View>
  );
}

